import { useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { FileText } from 'lucide-react';
import type { plans } from '@/lib/client';
import { planBrief, reflow } from '@/lib/brief';
import { useDocumentsStore } from '@/stores/documentsStore';
import { Prose } from './Prose';

/**
 * The plan at a glance, above its steps: what will change object by object, the answers given and what was left to
 * Zoom (from the document it was made from), and the document itself folded away, set back into paragraphs.
 */
export function PlanBrief({ plan }: { plan: plans.PlanView }) {
  const documentId = plan.document_id;
  const doc = useDocumentsStore((s) => (documentId === undefined ? undefined : s.byId[documentId]));
  const load = useDocumentsStore((s) => s.load);
  useEffect(() => {
    if (documentId !== undefined && doc === undefined) void load(documentId);
  }, [documentId, doc, load]);
  const brief = useMemo(() => planBrief(plan, doc ?? null), [plan, doc]);
  const source = useMemo(() => reflow(plan.goal), [plan.goal]);

  return (
    <section className="rounded-xl border border-border bg-card px-4 py-3">
      <Prose>{brief.main}</Prose>
      {brief.defaults.length > 0 && (
        <details className="mt-2">
          <summary className="cursor-pointer text-xs text-muted-foreground hover:text-foreground">Left to Zoom's defaults ({brief.defaults.length})</summary>
          <Prose className="mt-1">{brief.defaults.join('\n')}</Prose>
        </details>
      )}
      {plan.goal.trim() !== '' && (
        <details className="group mt-3 border-t border-border pt-2">
          <summary className="flex cursor-pointer list-none items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground">
            <FileText className="h-3.5 w-3.5" />
            <span className="group-open:hidden">Show the request</span>
            <span className="hidden group-open:inline">Hide the request</span>
            {doc && <span className="text-muted-foreground/70">· {doc.filename}</span>}
            {documentId !== undefined && <Link to={`/changes/documents/${documentId}`} className="ml-auto underline hover:text-foreground" onClick={(e) => e.stopPropagation()}>questions and answers</Link>}
          </summary>
          <div className="mt-2 max-h-80 overflow-y-auto whitespace-pre-line text-sm leading-relaxed text-muted-foreground">{source}</div>
        </details>
      )}
    </section>
  );
}
